/**
 * Refreshing the app after the BOT writes something (MDOT-24442).
 *
 * The browser knows its own writes. A mutating MCP tool call returns *here*, in this tab, at the
 * moment the mutation has landed — so the refresh it owes is a plain react-query invalidation, not
 * something to wait for on the SSE stream. That distinction is load-bearing, because SSE is a
 * recompute-STATUS feed rather than a change feed: no topic means "entity created" or "renamed"
 * (a bot duplicate + rename was measured emitting nothing at all), and whole families the bot
 * writes to have no topic in the first place — workspaces, classifications / custom attributes,
 * instrument fields and risk models, report templates, storyfolios. Waiting on an event that is
 * never emitted is how a bot write stays invisible until the user reloads the page.
 *
 * SSE keeps the half only the backend knows: the ASYNC AFTERMATH of a write — a recompute reaching
 * READY, an import finishing. Those stay per-page subscriptions and nothing here replaces them.
 * Invalidating at tool-return can therefore land a CALCULATING snapshot, which is correct: the
 * event that follows finishes the story.
 */
import { eventBus } from "$root/event-bus";
import { queryClient } from "$root/third-party-integrations/react-query/typedQueryClient";
import { seconds } from "$root/utils";
import debounce from "debounce";
import { type MCPTool, isReadOnlyTool, isTaskTool } from "./MCPHttpClient";
import { sphereAiConversationsRepositoryName } from "./repository";

/**
 * Chat tools (`plot_chart`, `calculator`, `radio`, `select`, `open_page`, `response_grep`) are
 * sphere-mcp's own, not MCP tools, so they are NOT in `toolsQuery.data` and write nothing. This
 * explicit list is a guard so that a same-named MCP tool could never trigger a refresh: these
 * names are never treated as mutating.
 */
export const CLIENT_ONLY_TOOL_NAMES: ReadonlySet<string> = new Set([
	"plot_chart",
	"calculator",
	"radio",
	"select",
	"open_page",
	"response_grep",
]);

/**
 * A tool counts as mutating if it's a task tool (task-augmentation is how the server exposes its
 * long-running *writes*), if it isn't marked read-only, or if it carries the destructive hint.
 * Note the polarity of the middle test: a missing `readOnlyHint` means mutating, so a server that
 * forgets the annotation costs us a redundant refresh rather than a stale screen.
 * Client-only tool names are excluded (see {@link CLIENT_ONLY_TOOL_NAMES}).
 */
export function isMutatingTool(tool: MCPTool): boolean {
	if (CLIENT_ONLY_TOOL_NAMES.has(tool.name)) return false;
	return isTaskTool(tool) || !isReadOnlyTool(tool) || tool.annotations?.destructiveHint === true;
}

/**
 * The set of server tool names whose successful execution owes a cache refresh, derived from the
 * MCP tool list. Read-only tools are left out; everything mutating/destructive/task-based is in.
 */
export function buildMutatingToolNames(tools: ReadonlyArray<MCPTool> | undefined): Set<string> {
	const names = new Set<string>();
	for (const tool of tools ?? []) {
		if (isMutatingTool(tool)) names.add(tool.name);
	}
	return names;
}

/**
 * Query keys owned by the chat itself, which a bot write must NEVER invalidate.
 *
 * `retrieveSelectedChatAIData` is the dangerous one: it reads the conversation's PERSISTED
 * snapshot and its success handler feeds it straight to `setMessages`, so refetching it mid-turn
 * would overwrite the live turn with whatever was last persisted server-side. The conversation
 * repository's list (the history list) and `sphere-mcp-tools` (the tool manifest, fetched once per
 * session) are simply not something a portfolio write can invalidate.
 */
export const CHAT_OWNED_QUERY_KEYS: ReadonlySet<string> = new Set([
	"retrieveSelectedChatAIData",
	sphereAiConversationsRepositoryName,
	"sphere-mcp-tools",
]);

/** Whether a cached query belongs to the chat itself (see {@link CHAT_OWNED_QUERY_KEYS}). */
export function isChatOwnedQueryKey(queryKey: ReadonlyArray<unknown>): boolean {
	const root = queryKey[0];
	return typeof root === "string" && CHAT_OWNED_QUERY_KEYS.has(root);
}

/** A uuid-shaped string, the only value we can recognise as an entity reference without a schema. */
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Every uuid-shaped string anywhere in a tool call's input.
 *
 * Deliberately structural rather than keyed: the workspace tools alone name their target `uuid`
 * (`workspace-update`, `workspace-delete`) *and* `workspaceUuid` (`workspace-rename`,
 * `workspace-move`), so reading a fixed field would silently miss half of them. Walking the input
 * for the SHAPE costs nothing and can't rot when a tool adds or renames a parameter.
 *
 * The trade-off is the opposite of a stale screen: this over-collects (a uuid that merely
 * *references* something — a concept, a template — is indistinguishable from the write's target),
 * so a listener may re-init once too often. That is a flash, not incorrect data.
 */
export function extractUuids(input: unknown, into: Set<string> = new Set()): Set<string> {
	if (typeof input === "string") {
		if (UUID_PATTERN.test(input)) into.add(input.toLowerCase());
		return into;
	}
	if (Array.isArray(input)) {
		for (const item of input) extractUuids(item, into);
		return into;
	}
	if (input !== null && typeof input === "object") {
		for (const value of Object.values(input)) extractUuids(value, into);
	}
	return into;
}

/**
 * Writes that have settled since the last flush, coalesced by the debounce below. Accumulated
 * module-level rather than passed through the debounce, because `debounce` forwards only the LAST
 * call's arguments — and a burst's earlier writes are exactly the ones whose uuids we need.
 */
const pendingWrites: { toolNames: Set<string>; uuids: Set<string> } = { toolNames: new Set(), uuids: new Set() };

/**
 * Invalidate everything a bot write could have touched: every cached query except the chat's own.
 *
 * Deliberately broad rather than tool-scoped. The 45 mutating tools name their target in 45
 * different shapes (a uuid, a name, a nested reference, nothing at all for a bulk edit), and a
 * write to one entity invalidates every list that entity can appear in anyway — so a per-tool
 * mapping would be a large, silently-rotting table whose failure mode is a stale screen.
 * `refetchType: "active"` is what keeps the breadth cheap: only queries with a mounted observer
 * refetch now, everything else is merely marked stale and refetches when it is next mounted.
 */
export async function invalidateAfterBotWrite(): Promise<void> {
	const settled = { toolNames: [...pendingWrites.toolNames], uuids: [...pendingWrites.uuids] };
	pendingWrites.toolNames.clear();
	pendingWrites.uuids.clear();

	await queryClient.invalidateQueries({
		refetchType: "active",
		predicate: (query) => !isChatOwnedQueryKey(query.queryKey),
	});

	// AFTER the await, which is load-bearing: `invalidateQueries` resolves once the active refetches
	// have settled, so a listener that re-reads the cache here sees the post-write state. Emitting
	// before it would hand every listener the data the write was supposed to replace.
	eventBus.emit("bot-write-settled", settled);
}

/**
 * How long a bot write waits before the caches are refreshed. This coalesces a turn's writes into
 * one invalidation — the model tends to write in bursts (create, then set the composition, then
 * rename) and each write on its own would re-invalidate the same caches. Module-level, so the
 * flush still happens if the user closes the chat drawer in between.
 */
const BOT_WRITE_INVALIDATION_DEBOUNCE = seconds(2);

const flushBotWriteInvalidation = debounce(() => {
	invalidateAfterBotWrite().catch((err) => {
		console.warn("sphere-ai: post-write invalidation failed", err);
	});
}, BOT_WRITE_INVALIDATION_DEBOUNCE);

/**
 * Queue the post-write refresh, coalescing writes that arrive close together.
 * Repeated invalidations are harmless (react-query's default `cancelRefetch: true` replaces an
 * in-flight refetch rather than racing it), so the debounce is about not doing redundant work.
 *
 * `input` is the tool call's arguments, mined for the uuids the settle event carries. It is optional
 * because the background-task path has none to give: `DeferredDelivery` carries no input, and
 * threading one through the persisted task map (which has to survive a reload) would buy nothing
 * today — every task tool is a `portfolio-studio` one, and no screen that needs the uuid hint copies
 * its server state the way the workspace does.
 */
export function scheduleInvalidateAfterBotWrite(toolName: string, input?: unknown): void {
	pendingWrites.toolNames.add(toolName);
	if (input !== undefined) extractUuids(input, pendingWrites.uuids);
	flushBotWriteInvalidation();
}
